import { TicketPercent } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import PageContainer from "shared/components/pagecontainer/PageContainer";
import { HeaderBusca } from "shared/components/headers/headerbusca/HeaderBusca";
import {
  buscadorCuponsConfig,
} from "shared/config/buscadorConfigs";

import CupomItem from "shared/components/cupomitem/CupomItem";

import { ICupom } from "shared/interfaces/ICupom";
import {
  buscarCuponsPorUsuario,
} from "shared/services/api/cupons.api";

import styles from "./MeusCupons.module.scss";

export const MeusCupons: React.FC = () => {
  const [loading, setLoading] = useState(true);

  const [listaCupons, setListaCupons] =
    useState<ICupom[]>([]);

  const [searchParams] = useSearchParams();

  /* ================================
     BUSCA
  ================================= */

  const busca =
    searchParams
      .get("search")
      ?.toLowerCase()
      .trim() || "";

  /* ================================
     CARREGAR CUPONS
  ================================= */

  async function carregarCupons() {
    try {
      setLoading(true);

      const cupons =
        await buscarCuponsPorUsuario();

      setListaCupons(cupons);

    } catch (error) {
      console.error(
        "Erro ao carregar cupons:",
        error
      );

    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregarCupons();
  }, []);

  /* ================================
     FILTRAR CUPONS
  ================================= */

  const cuponsFiltrados = useMemo(() => {
    if (!busca) {
      return listaCupons;
    }

    return listaCupons.filter((cupom) => {

      /*
       * Aqui usamos os campos que temos
       * disponíveis no cupom.
       *
       * Se seu ICupom tiver outros campos,
       * podemos adicionar depois.
       */

      const codigo =
        String(cupom.code || "")
          .toLowerCase();

      const descricao =
        String(cupom.descricao || "")
          .toLowerCase();

      return (
        codigo.includes(busca) ||
        descricao.includes(busca)
      );
    });

  }, [listaCupons, busca]);

  /* ================================
     VAZIO
  ================================= */

  const cuponsVazio =
    !loading &&
    listaCupons.length === 0;

  /* ================================
     BUSCA SEM RESULTADO
  ================================= */

  const nenhumResultado =
    !loading &&
    listaCupons.length > 0 &&
    cuponsFiltrados.length === 0;

  return (
    <PageContainer
      title="Meus Cupons"
      icon={<TicketPercent size={20} />}

      /*
       * Desktop:
       * substitui o buscador de produtos
       * pelo buscador de cupons.
       */
      showDesktopSearch={true}
      desktopSearchProps={
        buscadorCuponsConfig
      }

      /*
       * Mobile:
       * buscador + filtros/ordenação.
       */
      pageTools={
        <HeaderBusca
          buscadorProps={
            buscadorCuponsConfig
          }
          showFilter={true}
          showOrder={true}
          orderLabel="Ordenar A-Z"
        />
      }
    >

      <div className={styles.page}>

        {/* ================================
            LOADING
        ================================= */}

        {loading && (
          <p className={styles.loading}>
            Carregando cupons...
          </p>
        )}

        {/* ================================
            NENHUM CUPOM
        ================================= */}

        {cuponsVazio && (
          <div className={styles.vazioContainer}>

            <div className={styles.containerImg}>
              <img
                src="/imagens/semloja.png"
                alt="Nenhum cupom cadastrado"
              />
            </div>

            <h2 className={styles.vazio}>
              Você ainda não possui cupons.
            </h2>

          </div>
        )}

        {/* ================================
            NENHUM RESULTADO DA BUSCA
        ================================= */}

        {nenhumResultado && (
          <div className={styles.vazioContainer}>

            <h2 className={styles.vazio}>
              Nenhum cupom encontrado.
            </h2>

            <p className={styles.mensagem}>
              Tente buscar por outro código
              ou descrição.
            </p>

          </div>
        )}

        {/* ================================
            LISTA
        ================================= */}

        {!loading &&
          cuponsFiltrados.map(
            (cupom: ICupom, index: number) => (
              <CupomItem
                key={cupom.uid}
                cupom={cupom}
                numeroUI={String(
                  index + 1
                ).padStart(4, "0")}
              />
            )
          )}

      </div>

    </PageContainer>
  );
};